import { executeDictionaryFanOut } from './fan-out-engineering/dictionary-fan-out';
import { executeHubFanOut } from './fan-out-engineering/hub-fan-out';
import { executeJustLyricsFanOut } from './fan-out-engineering/justlyrics-fan-out';
import { executeSceneReaderFanOut } from './fan-out-engineering/scenereader-fan-out';
import { executePdfFanOut } from './fan-out-engineering/pdf-fan-out';
import { executeAskFliptFanOut } from './fan-out-engineering/askflipt-fan-out';

import { executeExportYtFanOut } from './fan-out-engineering/exportyt-fan-out';

export interface FanOutResult {
    synthesizedContext: string;
}

/**
 * Determines if a query is complex enough to warrant a Fan-Out execution.
 */
export function requiresFanOut(prompt: string, hasImage: boolean = false): boolean {
    const lowerMessage = prompt.toLowerCase();
    const wordCount = prompt.split(' ').length;
    const isHowTo = lowerMessage.includes("how to") || lowerMessage.includes("explain");
    const isBroad = lowerMessage.includes("what is the best way") || lowerMessage.includes("strategy");
    const hasYouTube = lowerMessage.includes("youtube.com") || lowerMessage.includes("youtu.be");
    const hasTikTok = lowerMessage.includes("tiktok") || /@[\w\.-]+/.test(prompt);
    const isTiktokArchiver = lowerMessage.includes('archive tiktok') || lowerMessage.includes('download tiktok channel') || lowerMessage.includes('tiktok archiver');
    const isJustRemove = lowerMessage.includes('find digital footprint') || lowerMessage.includes('scout username') || lowerMessage.includes('remove data broker') || lowerMessage.includes('opt out of') || lowerMessage.includes('justremove.me');
    const isDiary = lowerMessage.includes('diary') || lowerMessage.includes('journal') || lowerMessage.includes('remember on this day');
    const isFiraq = lowerMessage.includes('firaq') || lowerMessage.includes('court case') || lowerMessage.includes('litigation') || lowerMessage.includes('case entry');
    const isStegano = lowerMessage.includes('stegano') || lowerMessage.includes('embed file') || lowerMessage.includes('hidden message') || lowerMessage.includes('decode image') || lowerMessage.includes('embed message');
    
    return wordCount > 8 || isHowTo || isBroad || hasImage || hasYouTube || hasTikTok || isTiktokArchiver || isJustRemove || isDiary || isFiraq || isStegano;
}

/**
 * The Master Router for Fan-Out queries.
 * Determines WHICH project's Fan-Out engine needs to be spun up based on the seed prompt.
 */
export async function executeFanOutOrchestrator(seedPrompt: string, hasImage: boolean = false, pdfAttachments: any[] = []): Promise<FanOutResult> {
    const hasPdfs = pdfAttachments && pdfAttachments.length > 0;
    if (!requiresFanOut(seedPrompt, hasImage) && !hasPdfs) {
        return { synthesizedContext: "" };
    }

    const lowerPrompt = seedPrompt.toLowerCase();
    let finalContext = "";
    const tags: string[] = [];

    // Intent Routing
    if (lowerPrompt.includes('archive tiktok') || lowerPrompt.includes('download tiktok channel')) {
        tags.push('tiktok-archiver-trigger');
    }
    if (lowerPrompt.includes('digital footprint') || lowerPrompt.includes('remove data broker') || lowerPrompt.includes('opt out of')) {
        tags.push('justremove-trigger');
    }
    if (lowerPrompt.includes('diary') || lowerPrompt.includes('journal') || lowerPrompt.includes('remember on this day')) {
        tags.push('diary-trigger');
    }
    if (lowerPrompt.includes('firaq') || lowerPrompt.includes('court case') || lowerPrompt.includes('litigation') || lowerPrompt.includes('case entry')) {
        tags.push('firaq-trigger');
    }
    if (lowerPrompt.includes('stegano') || lowerPrompt.includes('embed file') || lowerPrompt.includes('hidden message') || lowerPrompt.includes('decode image') || lowerPrompt.includes('embed message')) {
        tags.push('stegano-trigger');
    }

    console.log('[FanOutOrchestrator] Request tagged for background routing:', tags);

    if (lowerPrompt.includes("youtube.com") || lowerPrompt.includes("youtu.be")) {
        finalContext += await executeExportYtFanOut(seedPrompt);
    }

    const { runTikTokArchiverFanOutEngine } = await import('./fan-out-engineering/tiktok-archiver-fan-out');
    if (runTikTokArchiverFanOutEngine(seedPrompt)) {
        const { buildTikTokArchiverContext } = await import('../contexts/tiktok-archiver-context');
        finalContext += buildTikTokArchiverContext();
    }
    
    if (tags.includes('justremove-trigger')) {
        const { executeJustRemoveFanOut } = await import('./fan-out-engineering/justremove-fan-out');
        finalContext += await executeJustRemoveFanOut(seedPrompt);
    }
    
    if (tags.includes('diary-trigger')) {
        const { executeDiaryFanOut } = await import('./fan-out-engineering/diary-fan-out');
        finalContext += await executeDiaryFanOut(seedPrompt);
    }
    
    if (tags.includes('firaq-trigger')) {
        const { executeFiraqFanOut } = await import('./fan-out-engineering/firaq-fan-out');
        finalContext += await executeFiraqFanOut(seedPrompt);
    }

    if (tags.includes('stegano-trigger')) {
        const { executeSteganoFanOut } = await import('./fan-out-engineering/stegano-fan-out');
        finalContext += await executeSteganoFanOut(seedPrompt);
    }

    if (lowerPrompt.includes("news") || lowerPrompt.includes("today") || lowerPrompt.includes("latest") || lowerPrompt.includes("economy")) {
        finalContext += await executeHubFanOut(seedPrompt);
    } 
    
    if (lowerPrompt.includes("slang") || lowerPrompt.includes("speak") || lowerPrompt.includes("gen z") || lowerPrompt.includes("word")) {
        finalContext += await executeDictionaryFanOut(seedPrompt);
    }

    if (lowerPrompt.includes("lyrics") || lowerPrompt.includes("song") || lowerPrompt.includes("music") || lowerPrompt.includes("track") || lowerPrompt.includes("play ") || lowerPrompt.includes("listen to")) {
        finalContext += await executeJustLyricsFanOut(seedPrompt);
    }

    if (hasImage) {
        finalContext += await executeSceneReaderFanOut(seedPrompt);
    }

    if (pdfAttachments && pdfAttachments.length > 0) {
        finalContext += await executePdfFanOut(seedPrompt, pdfAttachments);
        finalContext += await executeAskFliptFanOut(seedPrompt, pdfAttachments);
    }

    const hasMedia = (window as any).selectedAttachments?.some((a: any) => 
        a.type.startsWith('video/') || a.type.startsWith('audio/') || a.name.match(/\.(mp4|webm|mp3|wav|ogg|m4a)$/i)
    );

    if (hasMedia) {
        const { executeJustRecordFanOut } = await import('./fan-out-engineering/justrecord-fan-out');
        finalContext += await executeJustRecordFanOut(seedPrompt, true);
    }

    // If no specific intent matched, run both
    if (!finalContext) {
        const hubContext = await executeHubFanOut(seedPrompt);
        const dictContext = await executeDictionaryFanOut(seedPrompt);
        finalContext = hubContext + dictContext;
    }

    return { synthesizedContext: finalContext };
}
